import { Error as ErrorIcon, Home as HomeIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Paper, Typography } from '@mui/material';
import { Component, ErrorInfo, ReactNode } from 'react';

// Global render counter for infinite loop detection
declare global {
  interface Window {
    renderCount?: { [key: string]: number };
    lastRenderTime?: { [key: string]: number };
    infiniteLoopDetected?: boolean;
  }
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  /**
   * Detect stale chunk / deploy mismatch errors.
   * These happen when the server has been redeployed with new JS chunks but the
   * client still references old chunk hashes. The only safe recovery is a full
   * page reload so the browser fetches the new HTML with updated chunk URLs.
   */
  private static isChunkLoadError(error: Error): boolean {
    const msg = error.message || '';
    const name = error.name || '';
    return (
      name === 'ChunkLoadError' ||
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      // Next.js specific: when a page route can't load its JS bundle
      msg.includes('Failed to load static file') ||
      (msg.includes('Cannot find module') && msg.includes('.next'))
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // ─── STALE CHUNK AUTO-RECOVERY ───
    // If a deploy happened while the user had the page open, their browser
    // will try to fetch JS chunks that no longer exist. Instead of showing
    // an opaque "Something went wrong" page, do a silent full-page reload.
    // Guard: only attempt once per page load to avoid infinite reload loops.
    if (ErrorBoundary.isChunkLoadError(error)) {
      const reloadKey = '__chunk_reload_attempted';
      if (typeof window !== 'undefined' && !sessionStorage.getItem(reloadKey)) {
        console.warn(
          '[ErrorBoundary] 🔄 Stale chunk detected after deploy — auto-reloading page.',
          { message: error.message },
        );
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
        return; // Don't render error UI — reload is in progress
      }
      // If we already reloaded once and still failing, fall through to show error UI
      console.error(
        '[ErrorBoundary] 🚨 Stale chunk detected but reload already attempted. Showing error UI.',
        { message: error.message },
      );
    }

    // INFINITE LOOP DETECTION: Check for excessive renders
    const infiniteLoopInfo = this.detectInfiniteLoop(error);

    // ENHANCED LOGGING: Detailed error analysis for React context issues
    const errorCode = this.extractReactErrorCode(error.message);
    const contextMounts = this.getContextMountInfo();

    console.error('[ErrorBoundary] 🚨 CRITICAL ERROR CAUGHT:', {
      errorId: this.state.errorId,
      errorCode,
      message: error.message,
      stack: error.stack?.substring(0, 500),
      componentStack: errorInfo.componentStack?.substring(0, 500),
      contextMounts,
      infiniteLoopInfo,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : 'server',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    });

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report to monitoring service (if available)
    this.reportError(error, errorInfo);
  }

  private extractReactErrorCode(message: string): string | null {
    // Extract React error codes like #300, #310
    const match = message.match(/#(\d+)/);
    return match ? `#${match[1]}` : null;
  }

  private getContextMountInfo() {
    // Try to get context mount information from global state
    if (typeof window === 'undefined') return null;

    try {
      return {
        addressContextMounts: (window as any).addressContextMountCount || 0,
        cartContextMounts: (window as any).cartContextMountCount || 0,
        totalContextMounts: Object.keys(window).filter(
          (key) => key.includes('Context') && key.includes('Mount'),
        ).length,
      };
    } catch {
      return null;
    }
  }

  private detectInfiniteLoop(error: Error) {
    // Check if this is a React infinite loop error
    if (typeof window === 'undefined') return null;

    const isInfiniteLoop =
      error.message.includes('Too many re-renders') ||
      error.message.includes('#310') ||
      error.message.includes('Maximum update depth exceeded');

    if (!isInfiniteLoop) return null;

    // Initialize global counters if not exists
    if (!window.renderCount) window.renderCount = {};
    if (!window.lastRenderTime) window.lastRenderTime = {};

    const now = Date.now();
    const recentRenders = Object.entries(window.renderCount).filter(
      ([_, count]) => count > 10, // Components with >10 renders in short time
    );

    const rapidRenders = Object.entries(window.lastRenderTime).filter(
      ([component, time]) => now - time < 1000 && window.renderCount[component] > 5, // >5 renders per second
    );

    return {
      isInfiniteLoop: true,
      componentsWithExcessiveRenders: recentRenders.map(([comp, count]) => ({
        component: comp,
        renderCount: count,
      })),
      componentsWithRapidRenders: rapidRenders.map(([comp, time]) => ({
        component: comp,
        lastRender: time,
      })),
      totalTrackedComponents: Object.keys(window.renderCount).length,
      detectionTimestamp: now,
    };
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Import monitoring service dynamically to avoid hook issues in class component
      import('../services/MonitoringService').then(({ monitoringService }) => {
        monitoringService.captureError({
          message: error.message,
          stack: error.stack,
          component: 'ErrorBoundary',
          severity: 'high',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
          timestamp: new Date().toISOString(),
          context: {
            componentStack: errorInfo.componentStack,
            errorId: this.state.errorId,
            userId: this.getCurrentUserId(),
          },
        });
      });

      // Also log to console for development
      console.error('[ErrorBoundary] Error captured and sent to monitoring service:', {
        message: error.message,
        errorId: this.state.errorId,
        componentStack: errorInfo.componentStack?.substring(0, 200) + '...',
      });
    } catch (reportingError) {
      console.error('[ErrorBoundary] Failed to report error:', reportingError);
    }
  };

  private getCurrentUserId = (): string | null => {
    try {
      if (typeof window === 'undefined') return null;

      // Try Redux store first
      const reduxState = (window as any).__REDUX_STORE__?.getState?.();
      if (reduxState?.auth?.user?.id) {
        return reduxState.auth.user.id;
      }

      // Try localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user?.id || null;
      }

      return null;
    } catch {
      return null;
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  private handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  private handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              borderRadius: 2,
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />

            <Typography variant="h4" component="h1" gutterBottom color="error">
              Something went wrong
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We encountered an unexpected error. This has been reported to our team.
              {this.state.errorId && (
                <>
                  <br />
                  Error ID: <code>{this.state.errorId}</code>
                </>
              )}
            </Typography>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <AlertTitle>What you can try:</AlertTitle>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Click "Try Again" to reload this section</li>
                <li>Go back to the home page</li>
                <li>Refresh the entire page</li>
              </ul>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
              >
                Try Again
              </Button>

              <Button variant="outlined" startIcon={<HomeIcon />} onClick={this.handleGoHome}>
                Go Home
              </Button>

              <Button variant="text" onClick={this.handleReload}>
                Reload Page
              </Button>
            </Box>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Alert severity="warning" sx={{ mt: 3, textAlign: 'left' }}>
                <AlertTitle>Development Error Details:</AlertTitle>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    mt: 1,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Alert>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
